import {
  IonButtons,
  IonModal,
  IonTitle,
  IonToolbar,
  IonHeader,
  IonContent,
  IonIcon,
  IonText,
  IonSpinner,
  IonItem,
  IonLabel,
} from '@ionic/react';
import HStack from 'components/HStack';
import DynamicButton from 'components/buttons/DynamicButton';
import VStack from 'components/VStack';
import {
  locationOutline,
  flagOutline,
  star,
  trophyOutline,
  personCircleOutline,
  videocam,
} from 'ionicons/icons';
import {
  ComponentProps,
  FunctionComponent,
  useRef,
  useState,
  useEffect,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { ExploreElement } from '../../models/ExploreElementModel/ExploreElementModel';
import { RunModel } from '../../models/RunModel/RunModel';
import { useStores } from '../../models';
import { useCloudStorage } from '../../services/Database/CloudStorageService';
import { formatScore } from '../../util/freak_ranker';
import { Virtuoso } from 'react-virtuoso';
import './PersonModal.css';
import RunListItem from '../listElements/RunListItem/RunListItem';

export interface PersonModalProps extends ComponentProps<typeof IonModal> {
  isOpen: boolean;
  onDidDismiss: () => void;
  person?: ExploreElement | null;
  score?: number;
  onRunClick?: (run: RunModel) => void; // NEU
}

const PersonModal: FunctionComponent<PersonModalProps> = ({
  isOpen,
  onDidDismiss,
  person,
  score,
  onRunClick,
  ...rest
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const history = useHistory();
  const { recordList } = useStores();
  const cloudStorage = useCloudStorage();

  const [userRuns, setUserRuns] = useState<RunModel[]>([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [totalScore, setTotalScore] = useState(0);

  const handleClose = () => {
    onDidDismiss?.();
    modalRef.current?.dismiss();
  };

  // const navigateToRun = (trick: RunModel) => {
  //   handleClose();
  //   history.push(`/trick/${trick.id}`);
  // };

  useEffect(() => {
    if (!person) {
      setUserRuns([]);
      return;
    }
    setIsLoadingRuns(true);
    // Zuordnung: Versuche zuerst über UID, dann Fallback auf Name
    const runs = recordList.publicRuns.filter((run) => {
      // UID Matching (userId oder uid)
      const runUid = (run as any).uid || (run as any).userId;
      const personUid = (person as any).uid || (person as any).id;
      if (runUid && personUid) {
        return runUid === personUid;
      }
      // Fallback: Name
      return run.skater === person.name;
    });
    setUserRuns(runs);
    setIsLoadingRuns(false);
  }, [person, recordList.publicRuns]);

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      className="edit-modal"
      {...rest}
    >
      <IonHeader className="ion-no-border">
        {/* <div className="modal-image-svg">
          <img src={'/assets/images/Pic_Continent_Filter.svg'} alt="Header" />
        </div> */}
        <IonToolbar className="edit-modal-toptoolbar">
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={handleClose}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        style={{ '--background': 'transparent', '--border-colour': 0 }}
      >
        {person ? (
          <div className="person-modal-wrapper">
            {/* Person Header with Avatar and Basic Info, jetzt wie ProfileTopBar */}
            <div
              className="person-modal-header"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 24,
                marginBottom: 16,
              }}
            >
              <div
                className="person-avatar"
                style={{ position: 'relative', width: 64, height: 64 }}
              >
                <IonIcon
                  src="/assets/icons/Person_Iro.svg"
                  className="person-avatar-icon"
                  style={{ width: 64, height: 64 }}
                />
              </div>
              <div className="person-info" style={{ flex: 1 }}>
                <div className="person-header">
                  <VStack>
                    <IonText
                      className="person-name"
                      style={{ fontSize: 22, fontWeight: 700 }}
                    >
                      {person.name}
                    </IonText>
                    <div
                      className="person-score"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <IonIcon
                        icon={star}
                        className="score-icon"
                        style={{ color: '#FFD600' }}
                      />
                      <IonText
                        className="score-text"
                        style={{ fontWeight: 600 }}
                      >
                        {formatScore(score)}
                      </IonText>
                    </div>
                  </VStack>
                </div>
                <div
                  className="person-meta"
                  style={{
                    display: 'flex',
                    gap: 16,
                    marginTop: 4,
                  }}
                >
                  {person.location && (
                    <div
                      className="person-meta-item"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <IonIcon
                        icon={locationOutline}
                        className="person-meta-icon"
                        style={{ color: 'white' }}
                      />
                      <IonText className="person-meta-text">
                        {person.location}
                      </IonText>
                    </div>
                  )}
                  {person.nationality && (
                    <div
                      className="person-meta-item"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <IonIcon
                        icon={flagOutline}
                        className="person-meta-icon"
                        style={{ color: 'white' }}
                      />
                      <IonText className="person-meta-text">
                        {person.nationality}
                      </IonText>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Public Runs Section bleibt wie gehabt */}
            <div className="person-tricks-section">
              <h2>{t('PUBLIC_TRICKS')}</h2>
              {isLoadingRuns ? (
                <div className="loading-section">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100vh',
                    }}
                  >
                    <IonSpinner
                      name="crescent"
                      style={{ width: 48, height: 48 }}
                    />
                  </div>
                  <IonText>{t('LOADING_TRICKS')}</IonText>
                </div>
              ) : userRuns.length > 0 ? (
                <Virtuoso
                  style={{ height: '300px' }}
                  totalCount={userRuns.length}
                  itemContent={(index) => {
                    const item = userRuns[index];
                    return (
                      <div
                        className="trick-list-item"
                        style={{ height: '200px', marginTop: '8px' }}
                      >
                        <RunListItem
                          key={`person_${item.id}_${index}`}
                          item={item}
                          isPublic={true}
                          isFriend={true}
                          onClick={onRunClick}
                        />
                      </div>
                    );
                  }}
                />
              ) : (
                <div className="no-tricks-section">
                  <IonText>{t('NO_PUBLIC_TRICKS')}</IonText>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="person-modal-empty">
            <p>{t('NO_PERSON_SELECTED')}</p>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default PersonModal;
